import React, {useState} from 'react';
import {Accordion, AccordionButton} from 'react-bootstrap';
import Box from '@mui/material/Box';
import { ArrowAccordionUpIcon, DownArrowIcon } from '../buttons/IconButtons';

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * CustomAccordion component renders a styled accordion with custom header and body content.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - The title used to generate unique IDs for the accordion item.
 * @param {React.ReactNode} props.children - Content to render inside the accordion body.
 * @param {React.ReactNode} props.header - Content to render on the left side of the accordion header.
 * @param {Function} props.onClick - Function to be called when the accordion item is clicked.
 * @param {React.ReactNode} props.button - Content to render on the right side of the accordion header.
 * @param {string} props.currentKey - Event key for the current accordion item.
 * @param {string} [props.defaultActiveKey=""] - Event key for the initially expanded accordion item.
 * @param {string} [props.activeKey] - Event key for the currently expanded accordion item (controlled mode).
 * @param {React.ReactNode} [props.subtitle] - Optional subtitle displayed below the header.
 * @param {string} [props.classNameHeader=""] - Additional CSS classes for the header container.
 * @param {string} [props.classNameContainer="border-radius-8px box-shadow-shades-150"] - Additional CSS classes for the outer accordion container.
 * @param {string} [props.classNameAccordionItem=""] - Additional CSS classes for the accordion item.
 * @param {string} [props.classNameAccordionButton="card-shades-50"] - Additional CSS classes for the accordion button.
 * @param {string} [props.classNameAccordionBody=""] - Additional CSS classes for the accordion body.
 * @param {React.ReactNode} [props.customArrowOpen=<DownArrowIcon/>] - Custom icon for the expanded state.
 * @param {React.ReactNode} [props.customArrowClose=<ArrowAccordionUpIcon/>] - Custom icon for the collapsed state.
 * @param {Function} [onSelect=() => {}] - Callback function when a new accordion item is selected.
 * @returns {JSX.Element} The rendered accordion component
 */
const CustomAccordion = ({
    title,
    children,
    header,
    onClick,
    button,
    currentKey,
    defaultActiveKey = '',
    activeKey,
    onSelect = () => {},
    subtitle,
    classNameHeader="",
    classNameContainer="border-radius-8px box-shadow-shades-150",
    classNameAccordionItem="",
    classNameAccordionButton="card-shades-50",
    classNameAccordionBody="",
    customArrowOpen = <DownArrowIcon size={18} className="icon-color-shades-500" />,
    customArrowClose = <ArrowAccordionUpIcon size={18} className="icon-color-shades-500" />,
}) => {
    const key = title.replace(/\s+/g, '_');
    const expanded = activeKey === currentKey;
    const [activeParentKey, setActiveParentKey] = useState(null);

    const handleParentSelect = (eventKey) => {
        setActiveParentKey((prev) => (prev === eventKey ? null : eventKey));
        onSelect(eventKey)
    };

    return (
        <Accordion
            id={`ca_custom_accordion_${key}`}
            defaultActiveKey={defaultActiveKey}
            className={`${classNameContainer}`}
            activeKey={activeParentKey}
            onSelect={handleParentSelect}
            alwaysOpen={false}
            currentKey='0'
        >
            <Accordion.Item
                onClick={onClick}
                eventKey={currentKey}
                id={`ca_custom_accordion_simple_item_${key}`}
                className={`accordion-item ${classNameAccordionItem}`}
            >
                <AccordionButton id={`ca_custom_accordion_button_${key}`} className={`d-block shadow-none border-0 ${classNameAccordionButton}`}>
                    <div className={`align-items-center d-flex justify-content-between ${classNameHeader} `}>
                        <div className='left'>{header}</div>
                        <div className='text-right align-items-center d-flex'>
                            {button}
                            {expanded ? customArrowClose : customArrowOpen}
                        </div>
                    </div>
                    {subtitle &&
                    <div>
                        {subtitle}
                    </div>
                    }
                </AccordionButton>

                <Accordion.Body id={`ca_custom_accordion_simple_body_${key}`} className={`mb-0 pb-2 ${classNameAccordionBody}`}>
                    <Box id={`ca_custom_accordion_simple_box_${key}`}
                        className='col-12 custom-input-style'
                    >
                        {children}
                    </Box>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

export default CustomAccordion;
